import { get, post } from "./client";

/**
 * Client for the SOFTSUPP-506 contact-role end-date backfill endpoints. The
 * WP→PG migration imported WordPress-inactive contact roles (statuscode 2)
 * with `is_published = false` but `end_date` left NULL, so every place that
 * trusts `end_date IS NULL` to mean "active" still counts them as live roles.
 * The scan finds that mismatch; apply writes the end dates an operator
 * approves — both live on address-book, which owns the contact_roles rows.
 */

/**
 * Rows per `apply` request. Callers with a bigger batch must split it and send
 * the chunks one after another — the response is the only copy of each row's
 * pre-update state, so a gateway timeout on one oversized request would lose
 * the rollback data for the whole run.
 */
export const APPLY_CHUNK_SIZE = 100;

/**
 * The WP→PG cutover date, after which PG became authoritative for
 * contact_roles. The conventional end date for roles the migration imported
 * as already-inactive: they were dead in WP by this day at the latest.
 */
export const DEFAULT_END_DATE = "2026-07-20";

/** One candidate contact role: unpublished but still active (no end date). */
export type ScanRow = {
  contactRoleId: string;
  contactId: string;
  wpContactId: number;
  contactName: string;
  companyName: string;
  officeName: string;
  roleType: string | null;
  startDate: string | null;
  updatedAt: string;
  /** Touched by a real PG-side edit after the cutover — deserves a closer look. */
  editedAfterCutover: boolean;
  /** Ending this row leaves the contact with no active published role at all. */
  losesLastActiveRole: boolean;
};

export type ScanSummary = {
  total: number;
  editedAfterCutover: number;
  losesLastActiveRole: number;
  contacts: number;
};

export type ScanResponse = {
  rows: ScanRow[];
  summary: ScanSummary;
  requestId: string;
};

export type ApplyMode = "end" | "restore";

export type ApplyRow = {
  contactRoleId: string;
  /** The end date to write. Null is only meaningful in restore mode: it clears
   * `end_date`, putting the role back to active. */
  value: string | null;
};

export type ApplyRowStatus = "applied" | "noop" | "skipped_already_ended" | "invalid" | "error";

/**
 * One result per submitted row, in the SAME ORDER as the request — merge by
 * index, since contactRoleId is the only other key and a malformed upload
 * could repeat it.
 */
export type ApplyRowResult = {
  contactRoleId: string;
  status: ApplyRowStatus;
  /** Pre-update `end_date`. What the rollback CSV is built from. */
  previous: string | null;
  message?: string;
};

export type ApplyCounts = Record<ApplyRowStatus, number>;

export type ApplyResponse = {
  results: ApplyRowResult[];
  counts: ApplyCounts;
  requestId: string;
};

export const contactRoleEndDateBackfillService = {
  async scan(): Promise<ScanResponse> {
    return get<ScanResponse>("/v1/address-book/contact-role-end-date-backfill");
  },

  async apply(rows: ApplyRow[], mode: ApplyMode): Promise<ApplyResponse> {
    return post<ApplyResponse>("/v1/address-book/contact-role-end-date-backfill/apply", {
      mode,
      rows,
    });
  },
};
